import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FiMail, FiLock, FiClock, FiCheck, FiX, FiCheckCircle } from "react-icons/fi";
import { CgSpinner } from "react-icons/cg";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { sendEmail } from "../../services/auth.service";
import { toAlphanumeric } from "../../helpers/util";

const VerifyEmailPopup = ({ verificationToken, setUser, user }) => {
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [error, setError] = useState("");
  const [resendAttempts, setResendAttempts] = useState(0);
  const [resendTimer, setResendTimer] = useState(60);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [canResend, setCanResend] = useState(false);

  // Animation variants
  const pulseAnimation = {
    initial: { scale: 1 },
    pulse: {
      scale: [1, 1.02, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        repeatType: "reverse"
      }
    }
  };

  const iconAnimation = {
    initial: { scale: 1, rotate: 0 },
    animate: {
      scale: [1, 1.1, 1],
      rotate: [0, 5, -5, 0],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        repeatDelay: 2
      }
    }
  };

  const successIconAnimation = {
    initial: { scale: 0 },
    animate: {
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 10
      }
    }
  };

  useEffect(() => {
    if (otp.join("").length === 6) {
      if (otp.join("") === verificationToken) {
        setUser({
          email: "user@example.com",
          password: "password123",
        });
        toast.success("Email verified successfully!");
        setError("");
        setIsError(false);
      } else {
        setIsError(true);
        toast.error("Wrong OTP! Please try again.");
      }
    }
  }, [otp, verificationToken, setUser]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timerId = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timerId);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  useEffect(() => {
    if (resendAttempts > 0) {
      setCanResend(false);
      if (resendAttempts >= 2) {
        setResendTimer(300); // 5 minutes for subsequent attempts
      } else {
        setResendTimer(60); // 1 minute for first 2 attempts
      }
    }
  }, [resendAttempts]);

  const handleChange = (value, index) => {
    if (isNaN(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
    setIsError(false);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && otp[index] === "") {
      if (index > 0) {
        document.getElementById(`otp-${index - 1}`).focus();
      }
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0 || !canResend) return;
    
    setIsLoading(true);
    try {
      await sendEmail({
        email: user.email,
        code: toAlphanumeric(parseFloat(verificationToken)),
      });
      setResendAttempts(prev => prev + 1);
      toast.info("OTP has been resent! Please check your email.");
    } catch (error) {
      toast.error("Failed to resend OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.4, type: "spring", stiffness: 100 }}
        className="w-full max-w-md"
      >
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
          {/* Header with animated icon */}
          <div className="bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600 p-6 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"></div>
            
            <motion.div 
              variants={iconAnimation}
              initial="initial"
              animate="animate"
              className="w-16 h-16 mx-auto mb-3 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm"
            >
              <FiMail className="text-2xl text-white" />
            </motion.div>
            
            <h2 className="text-2xl font-bold text-white mb-1">
              Verify Email Address
            </h2>
            <p className="text-blue-100 text-sm">
              Complete your registration process
            </p>
          </div>

          {/* Content */}
          <div className="p-7">
            <p className="text-sm text-gray-600 dark:text-gray-300 text-center mb-6">
              We've sent a 6-digit verification code to your email address. 
              Please enter it below to verify your account.
            </p>

            {/* OTP Inputs */}
            <div className="flex justify-between mb-7 gap-2">
              {otp.map((digit, index) => (
                <motion.input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  value={digit}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  whileFocus={{ scale: 1.05, boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.2)" }}
                  whileHover={{ scale: 1.02 }}
                  className={`w-12 h-12 sm:w-14 sm:h-14 border-2 text-center text-xl font-bold rounded-xl transition-all
                    ${isError 
                      ? "border-red-500 bg-red-50 dark:bg-red-900/20 text-red-600 shake" 
                      : "border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                    } focus:outline-none focus:border-blue-500 dark:focus:border-blue-400`}
                  maxLength={1}
                />
              ))}
            </div>

            {/* Error Message */}
            {error && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center justify-center gap-2 mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800"
              >
                <FiX className="text-red-500 dark:text-red-400" />
                <p className="text-red-600 dark:text-red-300 text-sm">
                  {error}
                </p>
              </motion.div>
            )}

            {/* Resend Section */}
            <div className="space-y-4 mb-6">
              <button
                onClick={handleResendOtp}
                disabled={resendTimer > 0 || isLoading || !canResend}
                className={`w-full py-3 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2
                  ${(resendTimer === 0 && canResend && !isLoading)
                    ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 shadow-md hover:shadow-lg"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  }`}
              >
                {isLoading ? (
                  <>
                    <CgSpinner className="animate-spin text-base" />
                    Sending...
                  </>
                ) : (
                  <>
                    <FiMail className="text-sm" />
                    Resend Verification Code
                  </>
                )}
              </button>
              
              {/* Timer display */}
              {resendTimer > 0 && (
                <motion.div
                  variants={pulseAnimation}
                  initial="initial"
                  animate="pulse"
                  className="text-center bg-gray-50 dark:bg-gray-900/30 rounded-lg p-4"
                >
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <FiClock className="text-gray-500 dark:text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-300 font-medium">
                      Resend OTP available in {resendTimer} seconds
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {resendAttempts < 2 
                      ? "First 2 attempts: 60 seconds wait" 
                      : "Subsequent attempts: 5 minutes wait"
                    }
                  </p>
                </motion.div>
              )}

              {/* Success message when timer reaches 0 */}
              {resendTimer === 0 && canResend && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800"
                >
                  <div className="flex items-center justify-center gap-2">
                    <motion.div
                      variants={successIconAnimation}
                      initial="initial"
                      animate="animate"
                    >
                      <FiCheckCircle className="text-green-600 dark:text-green-400 text-lg" />
                    </motion.div>
                    <span className="text-sm text-green-700 dark:text-green-300 font-medium">
                      You can now resend the verification code
                    </span>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Helper Text */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-center text-xs text-gray-500 dark:text-gray-400 pt-4 border-t border-gray-200 dark:border-gray-700"
            >
              <p className="mb-2">Didn't receive the code? Check your spam folder or</p>
              <p className="mb-3">
                Contact support at{" "}
                <a 
                  href="mailto:support@example.com" 
                  className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                >
                  support@example.com
                </a>
              </p>
              
              {/* Status indicators */}
              <div className="flex items-center justify-center gap-4 mt-4">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span className="text-xs">Code Sent</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-xs">Verified</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                  <span className="text-xs">Pending</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer note */}
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center text-xs text-gray-500 dark:text-gray-400 mt-4"
        >
          This code expires in 15 minutes. Ensure you enter it promptly.
        </motion.p>
      </motion.div>

      {/* Add CSS for shake animation */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-3px); }
          20%, 40%, 60%, 80% { transform: translateX(3px); }
        }
        .shake {
          animation: shake 0.5s ease-in-out;
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
};

export default VerifyEmailPopup;